/**
 * browser-loop — the client. Ring buffer + flush scheduling.
 *
 * One singleton per page. Owned by `provider.tsx`. The class is the
 * orchestrator; the heavy lifting lives in:
 *   - identity.ts   — 5-tier ID model + UUIDv7
 *   - traceparent.ts — current trace mint/peek
 *   - sampling.ts   — per-session deterministic gate + tail-promote
 *   - scrub.ts      — closed PII catalog
 *   - transport.ts  — fetch + keepalive + retry
 *   - patches/*     — emit() call sites
 *
 * Flush triggers (any of):
 *   - flushIntervalMs elapsed (default 2000)
 *   - buffer ≥ maxBatchSize / 2 (half-full)
 *   - visibilitychange=hidden (about to background)
 *   - beforeunload (about to leave)
 *   - any event with severity >= error → IMMEDIATE flush + tail-promote
 *
 * Ring buffer discipline:
 *   - cap 256 (config.ringBufferCap)
 *   - overflow → drop OLDEST, but keep the most recent error
 *     (sampling.ts tail-promote relies on having the buffer when an
 *     error fires)
 *
 * If sampling says session is OUT, we still keep the ring buffer in
 * memory but DON'T flush until a tail-promote trigger fires. This is
 * the "I'm not interesting unless I error" promise made operational.
 */

import { readConfig, type BrowserLoopConfig, type BrowserLoopMode } from "./config";
import { getIdentity, newEventId } from "./identity";
import { scrubEvent } from "./scrub";
import { decideSampling } from "./sampling";
import { postBatch } from "./transport";
import { peekTrace } from "./traceparent";
import {
  BROWSER_EVENT_KIND,
  SEVERITY,
  type BrowserEventKind,
  type BrowserSeverity,
} from "./kinds";

interface EnvelopeFields {
  type: BrowserEventKind;
  severity?: BrowserSeverity;
  url?: string;
  // Optional per-kind extras; the schema is open client-side, the
  // backend's Pydantic discriminated union is the validator.
  [k: string]: unknown;
}

interface BufferedEvent {
  type: BrowserEventKind;
  severity: BrowserSeverity;
  device_id: string;
  session_id: string;
  tab_id: string;
  pageview_id: string;
  event_id: string;
  trace_id?: string;
  span_id?: string;
  request_id?: string;
  client_ts: string;
  url: string;
  referrer?: string;
  user_agent?: string;
  git_sha?: string;
  // per-kind payload spread in
  [k: string]: unknown;
}

export class BrowserLoopClient {
  private config: BrowserLoopConfig;
  private buffer: BufferedEvent[] = [];
  private droppedSinceLastFlush = 0;
  private flushTimer: number | null = null;
  private started = false;
  private sampledIn = false;
  private inflightFlush: Promise<void> | null = null;

  constructor(config: BrowserLoopConfig) {
    this.config = config;
  }

  start(): void {
    if (this.started || !this.config.enabled) return;
    this.started = true;

    const id = getIdentity();
    this.sampledIn = decideSampling(this.config, id.deviceId);

    // The first event we emit is loop.started — even when sampled-out
    // we keep it in the buffer so a future tail-promote can show the
    // session opened.
    this.emit({
      type: BROWSER_EVENT_KIND.loop_started,
      severity: SEVERITY.info,
      client_mode: this.config.mode,
      sampled_in: this.sampledIn,
      sample_rate: this.config.rumSampleRate,
    });

    // Periodic flush timer.
    this.flushTimer = window.setInterval(() => {
      void this.flush("interval");
    }, this.config.flushIntervalMs);

    // Visibility + unload triggers.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        void this.flush("visibility_hidden");
      }
    });
    window.addEventListener("beforeunload", () => {
      void this.flush("unload");
    });
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    if (this.flushTimer != null) {
      window.clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    void this.flush("interval");
  }

  /**
   * Public emit — every patch calls this. Pure-function up to the
   * buffer write; flush scheduling is decoupled.
   */
  emit(fields: EnvelopeFields): void {
    if (!this.started) return;

    const id = getIdentity();
    const trace = peekTrace();
    const sev = fields.severity ?? SEVERITY.info;

    // Pull per-kind extras out of the user's emit() args (everything
    // beyond the envelope keys) and spread them last so they don't
    // collide with the envelope fields.
    const { type: _t, severity: _s, url: _u, ...perKind } = fields;
    const ev: BufferedEvent = {
      type: fields.type,
      severity: sev,
      device_id: id.deviceId,
      session_id: id.sessionId,
      tab_id: id.tabId,
      pageview_id: id.pageviewId,
      event_id: newEventId(),
      trace_id: trace?.traceId,
      span_id: trace?.spanId,
      client_ts: new Date().toISOString(),
      url: fields.url ?? (typeof window !== "undefined" ? window.location.href : "<no-window>"),
      referrer:
        typeof document !== "undefined" && document.referrer
          ? document.referrer
          : undefined,
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      git_sha: this.config.gitSha ?? undefined,
      ...perKind,
    };

    // Source is NEVER sent from the client — server stamps it.
    delete (ev as Record<string, unknown>).source;

    // PII scrub (per-source level).
    scrubEvent(ev, this.config.mode);

    // Ring buffer write — drop oldest on overflow.
    if (this.buffer.length >= this.config.ringBufferCap) {
      this.buffer.shift();
      this.droppedSinceLastFlush++;
    }
    this.buffer.push(ev);

    // Tail-promote: any severity >= error in a sampled-OUT session
    // promotes the whole session retroactively. Set sampledIn so future
    // flushes ship.
    const isError = sev === SEVERITY.error || sev === SEVERITY.critical;
    if (isError && !this.sampledIn) {
      this.sampledIn = true;
      void this.flush("interval"); // ship the ring buffer immediately
    }

    // Half-full trigger.
    if (this.buffer.length >= this.config.maxBatchSize / 2) {
      void this.flush("buffer_half_full");
    } else if (isError) {
      // Errors always ship now (don't wait for the interval timer).
      void this.flush("interval");
    }
  }

  /**
   * Flush is reentrancy-safe: if a flush is in flight, the call returns
   * the same promise. The next flush gets scheduled when the current
   * one finishes.
   */
  private async flush(
    reason: "interval" | "buffer_half_full" | "visibility_hidden" | "unload",
  ): Promise<void> {
    if (this.inflightFlush) {
      return this.inflightFlush;
    }
    if (!this.sampledIn) return; // sampled out, ring buffer held in memory
    if (this.buffer.length === 0) return;

    const drain = this.buffer.splice(0, this.config.maxBatchSize);
    const droppedCount = this.droppedSinceLastFlush;
    this.droppedSinceLastFlush = 0;

    const payload = {
      events: drain,
      client_flushed_at: new Date().toISOString(),
      client_dropped_count: droppedCount,
      client_dropped_kind: droppedCount > 0 ? "buffer_full" : null,
      tail_promoted: false,
    };

    this.inflightFlush = (async () => {
      try {
        const result = await postBatch(
          this.config,
          payload,
          reason === "unload" ? "unload" : "hot",
        );
        if (!result.ok && result.status !== 0) {
          // Backend rejected the whole batch (4xx). Don't requeue — would
          // loop. The rejection is the diagnostic; future patches can
          // refine the payload shape.
          // 5xx after retry exhaustion: drop and emit one loop.dropped
          // synthetic so the agent sees we tried.
          if (result.status >= 500) {
            this.emit({
              type: BROWSER_EVENT_KIND.loop_dropped,
              severity: SEVERITY.warn,
              dropped_count: drain.length,
              drop_kind: "rate_limited",
            });
          }
        }
      } finally {
        this.inflightFlush = null;
      }
    })();

    return this.inflightFlush;
  }

  /** Diagnostics surface for /admin/dev-loop UI. */
  getState(): {
    started: boolean;
    mode: BrowserLoopMode;
    sampledIn: boolean;
    bufferLen: number;
    droppedSinceLastFlush: number;
  } {
    return {
      started: this.started,
      mode: this.config.mode,
      sampledIn: this.sampledIn,
      bufferLen: this.buffer.length,
      droppedSinceLastFlush: this.droppedSinceLastFlush,
    };
  }
}

// Module-singleton — the provider mounts/unmounts; the patches all
// import this same accessor.
let _client: BrowserLoopClient | null = null;

export function getClient(): BrowserLoopClient | null {
  return _client;
}

export function initClient(): BrowserLoopClient | null {
  if (_client) return _client;
  const config = readConfig();
  if (!config.enabled) return null;
  _client = new BrowserLoopClient(config);
  return _client;
}

export function teardownClient(): void {
  if (_client) {
    _client.stop();
    _client = null;
  }
}

/** Public emit helper used by patches and ErrorBoundary. No-op if no client. */
export function emit(fields: EnvelopeFields): void {
  _client?.emit(fields);
}
